import { DTOScimMeta } from "./meta";
import { z } from "zod";
// https://www.rfc-editor.org/rfc/rfc7643.html#section-4.1
// https://www.rfc-editor.org/rfc/rfc7643#page-19
const Bool = z.preprocess((val) => {
    if (typeof val === "string") {
        const lc = val.trim().toLowerCase();
        if (lc === "true")
            return true;
        if (lc === "false")
            return false;
    }
    if (typeof val === "number") {
        if (val === 1)
            return true;
        if (val === 0)
            return false;
    }
    return val;
}, z.boolean());
/**
 * RFC 7643 § 2.4 – generic multi-valued attribute shared by emails and photos
 */
export const DTOScimUserAttribute = z.preprocess((val) => {
    if (typeof val === "string") {
        return { value: val };
    }
    return val;
}, z.object({
    value: z.string().optional(),
    display: z.string().optional(),
    type: z.string().optional(),
    primary: Bool.optional(),
}));
export const DTOScimUserName = z.object({
    formatted: z.string().nullish().optional(),
    familyName: z.string().nullish().optional(),
    givenName: z.string().nullish().optional(),
    middleName: z.string().nullish().optional(),
    honorificPrefix: z.string().nullish().optional(),
    honorificSuffix: z.string().nullish().optional(),
});
// Accept any extension URN an IdP declares (e.g. enterprise); we require only core and ignore the rest
const ScimUserSchemas = z
    .array(z.string())
    .nonempty({ message: "`schemas` must not be empty" })
    .refine((arr) => arr.length === new Set(arr).size, {
    message: "duplicate values are not allowed",
})
    .refine((arr) => arr.includes("urn:ietf:params:scim:schemas:core:2.0:User"), {
    message: "core User schema URN is required",
});
export const DTOScimUserCore = z.object({
    id: z.string(),
    meta: DTOScimMeta.optional(),
    schemas: ScimUserSchemas.default([
        "urn:ietf:params:scim:schemas:core:2.0:User",
    ]),
});
export const DTOScimUserSingularAttributes = z.object({
    userName: z.string().min(1, "`userName` must not be empty"),
    externalId: z.string().nullish().optional(),
    displayName: z.string().nullish().optional(),
    nickName: z.string().nullish().optional(),
    profileUrl: z.string().nullish().optional(),
    preferredLanguage: z.string().nullish().optional(),
    locale: z.string().nullish().optional(),
    timezone: z.string().nullish().optional(),
    active: Bool.nullish().optional(),
});
export const DTOScimUserComplexSingularAttributes = z.object({
    name: DTOScimUserName.optional(),
});
export const DTOScimUserComplexMultivaluedAttributes = z.object({
    emails: z.array(DTOScimUserAttribute).optional(),
    photos: z.array(DTOScimUserAttribute).optional(),
});
export const DTOScimUser = DTOScimUserCore.merge(DTOScimUserSingularAttributes)
    .merge(DTOScimUserComplexSingularAttributes)
    .merge(DTOScimUserComplexMultivaluedAttributes);
export const DTOScimUserRequestBody = DTOScimUser.omit({ meta: true, id: true });
export const DTOScimUserResponse = DTOScimUser;
export const DTOScimUserListResponse = z.object({
    schemas: z.array(z.string()),
    Resources: z.array(DTOScimUser),
    totalResults: z.number(),
    startIndex: z.number(),
    itemsPerPage: z.number(),
});
